// BottomNav — barre de navigation basse MOBILE pour le site public (mobile-first).
// Persistante, adaptée à l'état de connexion, avec badge panier et zones tactiles ≥ 56px.
// Masquée en ≥ 768px (desktop garde le Header). N'apparaît que sur le site public
// (le portail admin/company/driver a sa propre UI et n'inclut pas ce composant).
import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../Context/AuthContext';
import { useCart } from '../../../Context/CartContext';
import './BottomNav.css';

// Icônes ligne 24×24 (stroke) — nettes, cohérentes, sans dépendance externe.
const Icon = ({ d, filled = false }) => (
  <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"
    fill={filled ? 'currentColor' : 'none'} stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const ICONS = {
  home:   'M3 10.5 12 3l9 7.5M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5',
  trips:  ['M4 17a2 2 0 1 0 4 0 2 2 0 0 0-4 0Z', 'M16 17a2 2 0 1 0 4 0 2 2 0 0 0-4 0Z', 'M6 17h8M8 7h8l2 6H6l2-6Z', 'M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
  cart:   ['M3 4h2l2.4 11.2a1 1 0 0 0 1 .8h8.2a1 1 0 0 0 1-.8L20 8H6', 'M9 20a1 1 0 1 0 2 0 1 1 0 0 0-2 0ZM16 20a1 1 0 1 0 2 0 1 1 0 0 0-2 0Z'],
  ticket: ['M4 7a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v3a2 2 0 0 0 0 4v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3a2 2 0 0 0 0-4V7Z', 'M14 6v12'],
  user:   ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z', 'M5 20a7 7 0 0 1 14 0'],
  login:  ['M15 3h4a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1h-4', 'M10 17l5-5-5-5', 'M15 12H3'],
};

const BottomNav = () => {
  const { t } = useTranslation('common');
  const { isAuthenticated, role, canUseCart } = useAuth();
  const { itemCount } = useCart();

  // La barre est réservée au parcours PASSAGER (client) et aux visiteurs non connectés.
  // Un chauffeur/admin connecté sur le site public ne voit pas les onglets passager.
  const isPassenger = !isAuthenticated || role === 'CLIENT';

  // Marque le body quand la barre est active → réserve l'espace bas (padding) et surélève
  // les FAB. Retiré au démontage / quand la barre n'est pas affichée (non-passager).
  useEffect(() => {
    document.body.classList.toggle('has-bottom-nav', isPassenger);
    return () => document.body.classList.remove('has-bottom-nav');
  }, [isPassenger]);

  if (!isPassenger) return null;

  const items = [
    { to: '/', end: true, icon: 'home',  label: t('nav.home') },
    { to: '/travels',      icon: 'trips', label: t('nav.trips') },
  ];

  if (isAuthenticated) {
    // 🛒 POINT 4 : panier via la condition CENTRALISÉE canUseCart (jamais un invité).
    if (canUseCart) items.push({ to: '/user/panier', icon: 'cart', label: t('bottomNav.cart'), badge: itemCount });
    items.push({ to: '/user/reservations', icon: 'ticket', label: t('bottomNav.trips') });
    items.push({ to: '/user/profile',      icon: 'user',   label: t('nav.profile') });
  } else {
    items.push({ to: '/contact', icon: 'user',  label: t('nav.contact') });
    items.push({ to: '/login',   icon: 'login', label: t('nav.login') });
  }

  return (
    <nav className="bottom-nav" role="navigation" aria-label={t('bottomNav.aria')}>
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          className={({ isActive }) => `bottom-nav__item${isActive ? ' is-active' : ''}`}
        >
          <span className="bottom-nav__icon">
            <Icon d={ICONS[it.icon]} />
            {it.badge > 0 && <span className="bottom-nav__badge">{it.badge > 9 ? '9+' : it.badge}</span>}
          </span>
          <span className="bottom-nav__label">{it.label}</span>
        </NavLink>
      ))}
    </nav>
  );
};

export default BottomNav;
